package com.essntl.features.itinerary.ui.edit_itinerary.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import core.dateformat.utils.formatTimestampToHHMM
import core.dateformat.utils.formatToFullDateTime
import core.dateformat.utils.stringToLocalDateTime
import com.essntl.features.category.domain.model.CategoryModel
import com.essntl.features.client.ui.ClientDialogButton
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.ui.edit_itinerary.*
import com.essntl.features.proposal.ui.allproposal.components.TravelAgentDialogButton
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.uikit.components.checkbox.EsCheckBox
import com.essntl.uikit.components.checkbox.EsCheckBoxColors
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.image.AsyncUrlIcon
import com.essntl.uikit.components.tooltip.EsTooltip
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.EsRowDefaultItem
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberFind
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.config.FeatureConfig
import core.dateformat.ui.formatIsoDateRange

@Composable
fun ItineraryServiceItem(
    state: EditItineraryState,
    onEvent: (EditItineraryEvent) -> Unit,
    onNavigation: (EditItineraryNavigation) -> Unit,
    isDndHover: Boolean,
    itineraryService: ItineraryServiceModel,
    isSelected: Boolean,
    isUpdateLoading: Boolean = false,
    isConcierge: Boolean,
    modifier: Modifier = Modifier,
) {
    val category = rememberFind(
        items = state.getAllCategory.data.orEmpty(),
        id = itineraryService.service.category,
        defaultValue = CategoryModel.empty,
    ) { cat ->
        cat.id
    }

    val tasks = state.rememberTasks(itineraryServiceId = itineraryService.id)
    val proposal = state.rememberProposal(itineraryId = itineraryService.itineraryId)
    val message = state.rememberMessage(itineraryServiceId = itineraryService.id)

    val interactionSource = remember { MutableInteractionSource() }

    val isHovered by interactionSource.collectIsHoveredAsState()

    val isPressed by interactionSource.collectIsPressedAsState()
    val contentAlpha = if (itineraryService.status == ItineraryServiceModel.Status.Cancelled) 0.3f else 1.0f

    Row(
        horizontalArrangement = if (isConcierge) Arrangement.spacedBy(2.spacing) else Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically,
        modifier = modifier
            .background(
                when {
                    itineraryService.status == ItineraryServiceModel.Status.Cancelled ->
                        MaterialTheme.esColorScheme.bgSecondary

                    isSelected ->
                        MaterialTheme.esColorScheme.tintedPressed

                    isPressed ->
                        MaterialTheme.esColorScheme.tintedPressed

                    isHovered ->
                        MaterialTheme.esColorScheme.tintedHover

                    isDndHover ->
                        MaterialTheme.esColorScheme.tintedPressed

                    else ->
                        MaterialTheme.esColorScheme.bgSecondary
                },
            )
            .padding(
                vertical = EsConstants.ListRowVerticalPadding,
                horizontal = EsConstants.ListRowHorizontalPadding,
            )
            .height(EsConstants.ListRowItemHeight)
            .pointerHoverIcon(PointerIcon.Hand)
            .focusProperties { canFocus = false }
            .clickable(
                interactionSource = interactionSource,
                indication = null,
            ) {
                onNavigation(EditItineraryNavigation.EditItineraryService(itineraryService))
            },
    ) {
        if (!isConcierge) {
            EsLargeIcon(
                res = EsIcons.Drag,
                modifier = Modifier
                    .graphicsLayer {
                        alpha =
                            if (isUpdateLoading)
                                0.3f
                            else
                                1.0f
                    },
            )
            Spacer(
                modifier = Modifier.width(1.5.dp),
            )
        }

        EsCheckBox(
            colors = EsCheckBoxColors(
                backgroundCheckedBoxColor = MaterialTheme.esColorScheme.primary,
                backgroundUncheckedBoxColor = MaterialTheme.esColorScheme.secondaryHover,
                borderCheckedBoxColor = Color.Transparent,
                checkMarkColor = Color.White,
                disabledCheckedBoxColor = MaterialTheme.esColorScheme.secondaryDisabled,
                disabledCheckMarkColor = MaterialTheme.esColorScheme.iconDisabled,
            ),
            checked = state.selectedItineraryServiceIds.contains(itineraryService.id),
            onCheckedChange = {
                onEvent(EditItineraryEvent.SelectItineraryService(itineraryService.id))
            },
            modifier = Modifier
                .focusProperties { canFocus = false },
        )

        // Itinerary Service title
        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .weight(2f)
                .fillMaxHeight()
                .padding(end = 0.5.spacing),
        ) {
            if (!category.iconUrl.isNullOrBlank()) {
                AsyncUrlIcon(
                    url = category.iconUrl ?: "",
                    contentDescription = category.name,
                    tint = if (itineraryService.linkedId == null) MaterialTheme.esColorScheme.primary else Color(
                        0xFF8985A2,
                    ),
                    modifier = Modifier
                        .alpha(contentAlpha)
                        .size(24.dp),
                )

                Spacer(modifier = Modifier.width(1f.spacing))
            }

            EsTooltip(
                text = itineraryService.service.title,
            ) {
                Column(
                    modifier = Modifier
                        .align(Alignment.CenterVertically)
                        .padding(start = 1.spacing),
                ) {
                    Text(
                        text = itineraryService.service.title,
                        color = MaterialTheme.esColorScheme.textPrimary,
                        style = MaterialTheme.esTypography.s2SemiBold16,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier
                            .alpha(contentAlpha)
                            .fillMaxWidth(0.85f),
                    )

                    Text(
                        text = itineraryService.supplier?.prettyName ?: "No supplier selected",
                        color = MaterialTheme.esColorScheme.textTertiary,
                        style = MaterialTheme.esTypography.p1Regular16,
                        textAlign = TextAlign.Start,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier
                            .alpha(contentAlpha)
                            .fillMaxWidth(0.85f),
                    )
                }
            }

            if (isConcierge) {
                Spacer(modifier = Modifier.weight(1f))

                EsTooltip(
                    text = "${proposal?.title}\nClick to preview",
                ) {
                    EsSmallOutlineIconButton(
                        onClick = {
                            onNavigation(EditItineraryNavigation.GetItinerary(itineraryService))
                        },
                        iconRes = EsIcons.Itinerary,
                        modifier = Modifier
                            .alpha(contentAlpha)
                            .focusProperties { canFocus = false },
                    )
                }
            }
        }

        // Client
        if (isConcierge) {
            EsRowDefaultItem(
                text = "",
                extraContent = {
                    ClientDialogButton(
                        client = state.rememberPrimaryClient(
                            itineraryId = itineraryService.itineraryId,
                        ),
                        onClick = {
                            onNavigation(EditItineraryNavigation.EditClient(it))
                        },
                    )
                },
                modifier = Modifier.alpha(contentAlpha),
            )
        }

        if (FeatureConfig.hasWhatsapp)
        // Message
            EsRowDefaultItem(
                text = "",
                extraContent = {
                    if (message == null)
                        EsLargeIcon(
                            res = EsIcons.Close,
                            tint = MaterialTheme.esColorScheme.iconSecondary,
                        )
                    else
                        EsTooltip(
                            text = "Sent ${stringToLocalDateTime(message.createdAt)?.formatToFullDateTime()}",
                        ) {
                            EsLargeIcon(
                                res = EsIcons.Messages,
                                tint = MaterialTheme.esColorScheme.iconAccent,
                            )
                        }
                },
            )

        // Status
        EsRowDefaultItem(
            text = "",
            extraContent = {
                itineraryService.status?.let {
                    ItineraryServiceStatusDropDown(
                        status = it,
                        onStatusSelected = { newStatus ->
                            if (newStatus != itineraryService.status)
                                onEvent(EditItineraryEvent.UpdateItineraryServiceStatus(itineraryService, newStatus))
                        },
                    )
                }
            },
        )

        if (!isConcierge) {

            // Dates
            val dates = listOf(
                itineraryService.startDate,
                itineraryService.endDate,
            ).formatIsoDateRange()

            EsRowDefaultItem(
                text = "",
                extraContent = {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center,
                    ) {
                        dates.mapIndexed { index, map ->
                            EsTooltip(
                                text = map["day"] ?: "",
                            ) {
                                Text(
                                    text = map["date"].orEmpty(),
                                    color = MaterialTheme.esColorScheme.textPrimary,
                                    style = MaterialTheme.esTypography.p2Medium14,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    modifier = Modifier
                                        .alpha(contentAlpha)
                                        .clip(shape = RoundedCornerShape(5.dp))
                                        .clickable {}
                                        .padding(0.5.spacing),
                                )
                            }

                            if (index != dates.lastIndex) {
                                Text("")
                            }
                        }
                    }
                },
            )
        }

        // Time
        EsRowDefaultItem(
            text = itineraryService.startTime?.let { startTime ->
                itineraryService.endTime?.let { endTime ->
                    "${formatTimestampToHHMM(startTime)} - ${formatTimestampToHHMM(endTime)}"
                } ?: formatTimestampToHHMM(startTime)
            },
            modifier = Modifier.alpha(contentAlpha),
        )

        // Tasks
        EsRowDefaultItem(
            text = "",
            extraContent = {
                if (tasks.isNotEmpty()) {
                    val todoTasks =
                        tasks.filter { it.status == TaskModel.Status.Todo }
                    val doneTasks =
                        tasks.filter { it.status == TaskModel.Status.Done }
                    val blockedTasks =
                        tasks.filter { it.status == TaskModel.Status.Blocked }
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.Center,
                    ) {
                        if (todoTasks.isNotEmpty()) {
                            EsTooltip(
                                text = TaskModel.Status.Todo.toString(),
                            ) {
                                Box(
                                    contentAlignment = Alignment.Center,
                                    modifier = Modifier
                                        .background(
                                            color = MaterialTheme.esColorScheme.textWarning,
                                            shape = CircleShape,
                                        )
                                        .width(30.dp)
                                        .height(30.dp),
                                ) {
                                    Text(todoTasks.size.toString())
                                }
                            }
                            Spacer(modifier = Modifier.width(0.5.spacing))
                        }

                        if (doneTasks.isNotEmpty()) {
                            EsTooltip(
                                text = TaskModel.Status.Done.toString(),
                            ) {
                                Box(
                                    contentAlignment = Alignment.Center,
                                    modifier = Modifier
                                        .background(
                                            color = MaterialTheme.esColorScheme.bgSuccess,
                                            shape = CircleShape,
                                        )
                                        .clip(CircleShape)
                                        .width(30.dp)
                                        .height(30.dp),
                                ) {
                                    Text(doneTasks.size.toString())
                                }
                            }
                            Spacer(modifier = Modifier.width(0.5.spacing))
                        }

                        if (blockedTasks.isNotEmpty()) {
                            EsTooltip(
                                text = TaskModel.Status.Blocked.toString(),
                            ) {
                                Box(
                                    contentAlignment = Alignment.Center,
                                    modifier = Modifier
                                        .alpha(contentAlpha)
                                        .background(
                                            color = MaterialTheme.esColorScheme.destructiveHover,
                                            shape = CircleShape,
                                        )
                                        .clip(CircleShape)
                                        .width(30.dp)
                                        .height(30.dp),
                                ) {
                                    Text(blockedTasks.size.toString())
                                }
                            }
                        }
                    }
                }
            },
        )

        // # Pax
        EsRowDefaultItem(
            text = itineraryService.guestCount.toString(),
            modifier = Modifier.alpha(contentAlpha),
        )

        // Travel Agent
        if (isConcierge) {
            EsRowDefaultItem(
                text = "",
                extraContent = {
                    TravelAgentDialogButton(
                        travelAgent = proposal?.travelAgent,
                        onClick = {
                            proposal?.let {
                                onNavigation(EditItineraryNavigation.EditTravelAgent(it))
                            }
                        },
                    )
                },
                modifier = Modifier.alpha(contentAlpha),
            )
        }
    }
}